/*
 * File: ProcedureFormSwitchList.js
 * Project: mp-mobile-app
 *
 * Copyright (c) 2019 MacPractice, Inc. All rights reserved.
 *
 * Description: Displays the list of diagnosis codes on the procedures form
 */
import React from "react";
import { List, Theme } from "@material-ui/core";
import { Droppable, DragDropContext, DropResult } from "react-beautiful-dnd";
import ProcedureFormSwitch from "./ProcedureFormSwitch";
import { withStyles } from "@material-ui/core/styles";
import { WithStyles } from "@material-ui/styles";
import { Instance } from "mobx-state-tree";
import ChargeModel from "../../store/ChargeModel";
import { observer } from "mobx-react";

const styles = (theme: Theme) => ({
  switchFields: {
    margin: `${theme.spacing(0)}px ${theme.spacing(2.5)}px ${theme.spacing(
      2.5
    )}px`
  }
});

interface Props extends WithStyles<typeof styles> {
  chargeModel: Instance<typeof ChargeModel>;
}

const ProcedureFormSwitchList = ({ classes, chargeModel }: Props) => {
  const sortedDiagnoses = chargeModel.sortedDiagnoses;

  const onDragEnd = (result: DropResult) => {
    const { destination, draggableId } = result;

    if (!destination) {
      return;
    }

    const draggedDiagnosisIndex = sortedDiagnoses.findIndex(
      diagnosis => "draggable" + diagnosis.umlsId === draggableId
    );
    if (draggedDiagnosisIndex <= -1) {
      return;
    }

    if (destination.index === draggedDiagnosisIndex) {
      return;
    }

    const draggedDiagnosis = sortedDiagnoses[draggedDiagnosisIndex];

    const newDiagnoses = Array.from(sortedDiagnoses);
    newDiagnoses.splice(draggedDiagnosisIndex, 1);
    newDiagnoses.splice(destination.index, 0, draggedDiagnosis);
    chargeModel.setSortedDiagnoses(newDiagnoses);
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId={"diagnosisList"}>
        {provided => (
          <List
            className={classes.switchFields}
            ref={provided.innerRef}
            {...provided.droppableProps}
          >
            {sortedDiagnoses.map((formDiagnosis, idx) => {
              return (
                <ProcedureFormSwitch
                  key={idx}
                  formDiagnosis={formDiagnosis}
                  chargeModel={chargeModel}
                />
              );
            })}
            {provided.placeholder}
          </List>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export default withStyles(styles)(observer(ProcedureFormSwitchList));
